import type { Dayjs, ConfigType, OptionType } from 'dayjs'
import dayjs from 'dayjs'

// Возвращает дату в формате ISO 8601 с часовым поясом, напр. «2024-01-31T15:30:00+03:00»
export function toISO8601String(date: Date): string {
  return dayjs(date).format('YYYY-MM-DDTHH:mm:ssZ')
}

// Возвращает локализованную дату по формату «L», напр. «01/31/2024»
export function toDateFormat(value: ConfigType, format?: OptionType): string {
  return dayjs(value, format).format('L')
}

// Возвращает дату в формате «ДД.ММ.ГГГГ», напр. «31.01.2024»
export function toDateFormatComma(value: ConfigType): string {
  return dayjs(value).format('DD.MM.YYYY')
}

// Возвращает дату и время в локализованном формате «L LT», напр. «01/31/2024 15:30»
export function toDateTimeFormat(value: ConfigType, format?: OptionType): string {
  return dayjs(value, format).format('L LT')
}

// Возвращает день и месяц прописью, напр. «31 January»
export function toMonthDateFormat(value: ConfigType, format?: OptionType): string {
  return dayjs(value, format).format('D MMMM')
}

// Возвращает день, месяц прописью и время, напр. «31 January, 15:30»
export function toDateAndTime(value: ConfigType, format?: OptionType): string {
  return dayjs(value, format).format('D MMMM, HH:mm')
}

// Возвращает день, месяц прописью и год с частицей «г.», напр. «31 January 2024 г.»
export function toMonthYearDateFormat(value: ConfigType, format?: OptionType): string {
  return dayjs(value, format).format('D MMMM YYYY г.')
}

// Возвращает день, месяц прописью и год, напр. «31 January 2024»
export function toMonthYearDateFormatShort(value: ConfigType, format?: OptionType): string {
  return dayjs(value, format).format('D MMMM YYYY')
}

// Возвращает месяц прописью и год, напр. «January 2024»
export function toMonthAndYear(value: ConfigType, format?: OptionType): string {
  return dayjs(value, format).format('MMMM YYYY')
}

// Возвращает время с секундами, напр. «15:30:00»
export function toTimeFormat(value: ConfigType, format?: OptionType): string {
  return dayjs(value, format).format('HH:mm:ss')
}

// Возвращает время без секунд, напр. «15:30»
export function toTimeShortFormat(value: ConfigType, format?: OptionType): string {
  return dayjs(value, format).format('HH:mm')
}

// Возвращает дату в формате API «ГГГГ-ММ-ДД», напр. «2024-01-31»
export function toApiDateFormat(value: ConfigType, format?: OptionType): string {
  return dayjs(value, format).format('YYYY-MM-DD')
}

// Возвращает день, месяц прописью и время, напр. «31 January, 15:30»
export function toApiDateTimeFormatHour(value: ConfigType): string {
  return dayjs(value).format('D MMMM, HH:mm')
}

// Возвращает дату и время в формате API, напр. «2024-01-31 15:30:00»
export function toApiDateTimeFormat(value: ConfigType, format?: OptionType): string {
  return dayjs(value, format).format('YYYY-MM-DD HH:mm:ss')
}

// Переводит количество секунд от начала суток в «ЧЧ:ММ:СС», напр. 3661 → «01:01:01»
export function secondsToTimeFormat(value: number): string {
  return toTimeFormat(dayjs().startOf('day').add(value, 'seconds'))
}

// Возвращает короткую дату «ДД.ММ», напр. «31.01»
export function toDateShortFormat(value: ConfigType, format?: OptionType): string {
  return dayjs(value, format).format('DD.MM')
}

// Возвращает полную дату в ISO-формате со временем и зоной, напр. «2024-01-31T12:30:00.000Z»
export function toISOFormat(value: ConfigType, format?: OptionType): string {
  return dayjs(value, format).toISOString()
}

export function getAcademicYear(date?: ConfigType): string {
  const now = dayjs(date)
  const startYear = now.month() >= 8 ? now.year() : now.year() - 1

  return `${startYear}/${startYear + 1}`
}

// Возвращает объект Dayjs с добавленными месяцами
export function addMonths(date: ConfigType, count: number): Dayjs {
  return dayjs(date).add(count, 'month')
}

// Возвращает объект Dayjs с добавленными днями
export function addDays(date: ConfigType, count: number): Dayjs {
  return dayjs(date).add(count, 'days')
}

// Возвращает объект Dayjs с добавленными часами
export function addHours(date: ConfigType, count: number): Dayjs {
  return dayjs(date).add(count, 'hours')
}
